import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, browserLocalPersistence, setPersistence } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

const REQUIRED_CONFIG_KEYS = ["apiKey", "authDomain", "projectId", "appId"];
const IS_PRODUCTION =
  (import.meta.env?.MODE || process.env.NODE_ENV) === "production";

if (typeof window !== "undefined" && !IS_PRODUCTION) {
  window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

const isValidConfiguration = (config) =>
  REQUIRED_CONFIG_KEYS.every((key) => config?.[key]);

export const getEnvironmentVariable = (key) =>
  import.meta.env?.[key] || process.env[key];

async function fetchHostingConfig(timeout = 1500) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch("/__/firebase/init.json", {
      cache: "no-store",
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const config = await response.json();
    return isValidConfiguration(config) ? config : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

function getEnvironmentConfiguration() {
  if (IS_PRODUCTION) return null;
  const config = {
    apiKey:            getEnvironmentVariable("REACT_APP_FIREBASE_API_KEY"),
    authDomain:        getEnvironmentVariable("REACT_APP_FIREBASE_AUTH_DOMAIN"),
    projectId:         getEnvironmentVariable("REACT_APP_FIREBASE_PROJECT_ID"),
    appId:             getEnvironmentVariable("REACT_APP_FIREBASE_APP_ID"),
    messagingSenderId: getEnvironmentVariable("REACT_APP_FIREBASE_MESSAGING_SENDER_ID"),
    measurementId:     getEnvironmentVariable("REACT_APP_FIREBASE_MEASUREMENT_ID"),
    storageBucket:     getEnvironmentVariable("REACT_APP_FIREBASE_STORAGE_BUCKET"),
  };
  return isValidConfiguration(config) ? config : null;
}

let firebaseApp;
async function initializeFirebaseApp() {
  if (getApps().length) return getApp();

  if (!firebaseApp) {
    const config = (await fetchHostingConfig()) || getEnvironmentConfiguration();
    if (!config) {
      throw new Error(
        "Firebase configuration not found. " +
          "Either start Firebase Hosting emulator or configure environment variables.",
      );
    }

    firebaseApp = initializeApp(config);

    if (typeof window !== "undefined") {
      try {
        initializeAppCheck(firebaseApp, {
          provider: new ReCaptchaEnterpriseProvider(
            getEnvironmentVariable("REACT_APP_RECAPTCHA_SITE_KEY"),
          ),
          isTokenAutoRefreshEnabled: true,
        });
      } catch (e) {
        console.warn("App Check initialisation failed:", e);
      }
    }
  }

  return firebaseApp;
}

let firebaseAuth;
export async function getAuthAsync() {
  if (firebaseAuth) return firebaseAuth;
  const app = await initializeFirebaseApp();
  const auth = getAuth(app);
  await setPersistence(auth, browserLocalPersistence);
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    try {
      auth.settings.appVerificationDisabledForTesting = true;
    } catch {
      // Ignore
    }
  }
  firebaseAuth = auth;
  return auth;
}

export async function getDatabaseAsync() {
  const app = await initializeFirebaseApp();
  return getFirestore(app);
}

export async function getFirebaseConfigAsync() {
  const app = await initializeFirebaseApp();
  return app.options;
}

export const storagePromise = initializeFirebaseApp().then((app) =>
  getStorage(app),
);
